import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { obtenerDetalleFactura, cancelarFactura, enviarCorreoFactura } from "../services/facturaService";
import { QRCodeCanvas } from "qrcode.react";
import Toast, { useToast } from "./Toast";
import "./FacturaDetallePrint.css";

/* ================= HELPERS ================= */

const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    return new Date(dateString).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    return String(dateString).slice(0, 10);
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return "";
  try {
    return new Date(dateString).toLocaleString("es-MX", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return String(dateString);
  }
};

const formatCurrency = (n, isDollar = false) =>
  (Number(n) || 0).toLocaleString("es-MX", {
    style: "currency",
    currency: isDollar ? "USD" : "MXN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function DetailItem({ label, value, span = 1 }) {
  return (
    <div className={`cfdi-field span-${span}`}>
      <p className="cfdi-field-label">{label}</p>
      <p className="cfdi-field-value">{value}</p>
    </div>
  );
}

/* ================= COMPONENT ================= */

export default function FacturaDetalle40() {
  const navigate = useNavigate();
  const { serie, factura, empresa } = useParams();

  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [folioSustitucion, setFolioSustitucion] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ destinatario: "", asunto: "", cuerpo: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const { toasts, closeToast, error: toastError, warning: toastWarning, success: toastSuccess } = useToast();

  useEffect(() => {
    let cancel = false;

    const loadFactura = async () => {
      try {
        setLoading(true);
        const result = await obtenerDetalleFactura({
          serie,
          factura: parseInt(factura, 10),
          empresa: parseInt(empresa, 10),
        });
        if (!cancel) setDetalle(result);
      } catch (err) {
        if (!cancel) setError("No se pudo cargar la factura.");
      } finally {
        if (!cancel) setLoading(false);
      }
    };

    loadFactura();
    return () => {
      cancel = true;
    };
  }, [serie, factura, empresa]);

  const computed = useMemo(() => {
    if (!detalle) return null;

    const isDollar = detalle.moneda === "D";
    const subtotal =
      detalle.partidas?.reduce((acc, p) => acc + Number(p.importe || 0), 0) ||
      0;

    const total = Number(detalle.importeTotal) || subtotal;
    const iva = total - subtotal;

    return { isDollar, subtotal, total, iva };
  }, [detalle]);

  const handleAbrirEmail = () => {
    const numDoc = `${detalle?.serie}-${detalle?.numeroDocumento}`;
    setEmailForm({
      destinatario: detalle?.emailCliente || "",
      asunto: `Factura ${numDoc} – LMD Integradora`,
      cuerpo: `Estimado(a) ${detalle?.nombreCliente || ""},\n\nAdjuntamos la factura ${numDoc} para su revisión.\n\nSaludos,\nLMD Integradora`,
    });
    setShowEmailModal(true);
  };

  const handleEnviarEmail = async () => {
    // Acepta varios correos separados por ';' o ',' — se valida cada uno
    const correos = (emailForm.destinatario || "").split(/[;,]/).map((s) => s.trim()).filter(Boolean);
    if (correos.length === 0) { toastWarning("Ingresa el correo del destinatario."); return; }
    const invalidos = correos.filter((c) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c));
    if (invalidos.length > 0) { toastWarning(`Correo(s) inválido(s): ${invalidos.join(", ")}`); return; }
    try {
      setSendingEmail(true);
      await enviarCorreoFactura({
        serie: detalle.serie,
        factura: detalle.numeroDocumento,
        empresa: parseInt(empresa, 10),
        destinatario: emailForm.destinatario.trim(),
        asunto: emailForm.asunto,
        cuerpo: emailForm.cuerpo,
      });
      setShowEmailModal(false);
      toastSuccess(`Correo enviado a ${emailForm.destinatario}`);
    } catch (err) {
      toastError(err?.response?.data?.detail || err?.response?.data || err?.message || "No se pudo enviar el correo");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCancelar = async () => {
    setCancelando(true);
    setCancelError(null);
    try {
      await cancelarFactura({
        serie,
        factura: parseInt(factura, 10),
        empresa: parseInt(empresa, 10),
        motivo: motivo || null,
        folioSustitucion: motivo === "01" ? (folioSustitucion.trim() || null) : null,
      });
      setShowConfirmCancel(false);
      setMotivo("");
      setFolioSustitucion("");
      setDetalle((d) => ({ ...d, status: "C" }));
    } catch (err) {
      setCancelError(err?.response?.data?.detail || err?.response?.data?.message || err.message || "Error al cancelar.");
    } finally {
      setCancelando(false);
    }
  };

  if (loading) return <div className="cfdi-page-pad">Cargando…</div>;
  if (error) return <div className="cfdi-page-pad cfdi-error">{error}</div>;
  if (!detalle || !computed)
    return <div className="cfdi-page-pad">No encontrada.</div>;

  const { isDollar, subtotal, total, iva } = computed;
  const esTimbrada = !!detalle.folioFiscalUuid;

  return (
    <div className="cfdi-page">
      {/* TOOLBAR */}
      <div className="cfdi-toolbar no-print">
        <button
          onClick={() => navigate("/facturacion/factura40")}
          className="cfdi-link-button"
        >
          ← Volver a Facturación
        </button>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {detalle.status === "C" && (
            <span style={{
              fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 99,
              background: "#fee2e2", color: "#b91c1c", border: "1px solid #fca5a5",
              letterSpacing: 0.4,
            }}>
              CANCELADA
            </span>
          )}

          {detalle.status !== "C" && (
            <button
              onClick={() => { setCancelError(null); setShowConfirmCancel(true); }}
              style={{
                fontSize: 12, fontWeight: 700, padding: "6px 14px", borderRadius: 4,
                background: "#dc2626", color: "#fff", border: "none", cursor: "pointer",
              }}
            >
              Cancelar Factura
            </button>
          )}

          <button
            onClick={handleAbrirEmail}
            style={{
              fontSize: 12, fontWeight: 700, padding: "6px 14px", borderRadius: 4,
              background: "#eff6ff", color: "#2563eb", border: "1px solid #bae6fd", cursor: "pointer",
            }}
          >
            ✉ Enviar por correo
          </button>

          <button
            onClick={() =>
              window.open(
                `/facturacion/print/${serie}/${factura}/${empresa}`,
                "_blank",
              )
            }
            className="cfdi-print-button"
          >
            🖨 Imprimir
          </button>
        </div>
      </div>

      {/* MODAL CONFIRMACIÓN CANCELACIÓN */}
      {showConfirmCancel && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowConfirmCancel(false); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            zIndex: 2000, display: "flex", justifyContent: "center", alignItems: "center",
          }}
        >
          <div style={{
            background: "#fff", borderRadius: 8, padding: 28, width: "90%", maxWidth: 440,
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 17, fontWeight: 700, color: "#111" }}>
              Confirmar Cancelación
            </h3>
            <p style={{ margin: "0 0 12px", fontSize: 14, color: "#374151" }}>
              Factura: <strong>{serie}-{factura}</strong> — {detalle.nombreCliente}
            </p>
            {esTimbrada ? (
              <div style={{
                background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 6,
                padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#92400e",
              }}>
                Esta factura tiene <strong>timbre fiscal (CFDI)</strong>. Se enviará la solicitud de cancelación al SAT a través del PAC.
              </div>
            ) : (
              <div style={{
                background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6,
                padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#0c4a6e",
              }}>
                Esta factura <strong>no tiene timbre fiscal</strong>. Se cancelará únicamente en la base de datos.
              </div>
            )}
            {/* Motivo de cancelación (obligatorio para timbradas) */}
            {esTimbrada && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                  Motivo de cancelación <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <select
                  value={motivo}
                  onChange={(e) => { setMotivo(e.target.value); setFolioSustitucion(""); }}
                  style={{
                    width: "100%", padding: "7px 10px", borderRadius: 4, fontSize: 13,
                    border: "1px solid #d1d5db", background: "#fff", color: "#111",
                  }}
                >
                  <option value="">-- Selecciona un motivo --</option>
                  <option value="01">01 – Comprobante emitido con errores con relación</option>
                  <option value="02">02 – Comprobante emitido con errores sin relación</option>
                  <option value="03">03 – No se llevó a cabo la operación</option>
                  <option value="04">04 – Operación nominativa relacionada en factura global</option>
                </select>
                {motivo === "01" && (
                  <div style={{ marginTop: 10 }}>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                      Folio fiscal de sustitución <span style={{ color: "#dc2626" }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={folioSustitucion}
                      onChange={(e) => setFolioSustitucion(e.target.value)}
                      placeholder="UUID de la factura que la sustituye"
                      style={{
                        width: "100%", padding: "7px 10px", borderRadius: 4, fontSize: 13,
                        border: "1px solid #d1d5db", boxSizing: "border-box",
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {cancelError && (
              <div style={{
                background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6,
                padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#b91c1c",
              }}>
                {cancelError}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={() => { setShowConfirmCancel(false); setMotivo(""); setFolioSustitucion(""); setCancelError(null); }}
                disabled={cancelando}
                style={{
                  padding: "8px 20px", borderRadius: 4, border: "1px solid #d1d5db",
                  background: "#f9fafb", color: "#374151", cursor: "pointer", fontSize: 13, fontWeight: 600,
                }}
              >
                No, volver
              </button>
              <button
                onClick={handleCancelar}
                disabled={cancelando || (esTimbrada && (!motivo || (motivo === "01" && !folioSustitucion.trim())))}
                style={{
                  padding: "8px 20px", borderRadius: 4, border: "none",
                  background: (cancelando || (esTimbrada && (!motivo || (motivo === "01" && !folioSustitucion.trim()))))
                    ? "#e5e7eb" : "#dc2626",
                  color: (cancelando || (esTimbrada && (!motivo || (motivo === "01" && !folioSustitucion.trim()))))
                    ? "#6b7280" : "#fff",
                  cursor: cancelando ? "wait" : "pointer", fontSize: 13, fontWeight: 700,
                }}
              >
                {cancelando ? "Cancelando..." : "Sí, cancelar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ENVIAR CORREO */}
      {showEmailModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowEmailModal(false); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            zIndex: 2000, display: "flex", justifyContent: "center", alignItems: "center",
          }}
        >
          <div style={{
            background: "#fff", borderRadius: 8, padding: 28, width: "90%", maxWidth: 500,
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 17, fontWeight: 700, color: "#111" }}>
              Enviar Factura por correo
            </h3>

            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
              Destinatario
            </label>
            <input
              type="text"
              value={emailForm.destinatario}
              onChange={(e) => setEmailForm((f) => ({ ...f, destinatario: e.target.value }))}
              disabled={sendingEmail}
              placeholder="correo1@empresa.com;correo2@empresa.com"
              style={{ width: "100%", padding: "8px 10px", borderRadius: 4, border: "1px solid #d1d5db", fontSize: 13, boxSizing: "border-box", marginBottom: 12 }}
            />

            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
              Asunto
            </label>
            <input
              value={emailForm.asunto}
              onChange={(e) => setEmailForm((f) => ({ ...f, asunto: e.target.value }))}
              disabled={sendingEmail}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 4, border: "1px solid #d1d5db", fontSize: 13, boxSizing: "border-box", marginBottom: 12 }}
            />

            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
              Mensaje
            </label>
            <textarea
              rows={6}
              value={emailForm.cuerpo}
              onChange={(e) => setEmailForm((f) => ({ ...f, cuerpo: e.target.value }))}
              disabled={sendingEmail}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 4, border: "1px solid #d1d5db", fontSize: 13, boxSizing: "border-box", resize: "vertical", marginBottom: 20, fontFamily: "inherit" }}
            />

            <p style={{ margin: "0 0 16px", fontSize: 12, color: "#6b7280" }}>
              Se adjuntará el PDF y el XML de la factura.
            </p>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={() => setShowEmailModal(false)}
                disabled={sendingEmail}
                style={{ padding: "8px 20px", borderRadius: 4, border: "1px solid #d1d5db", background: "#f9fafb", color: "#374151", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
              >
                Cancelar
              </button>
              <button
                onClick={handleEnviarEmail}
                disabled={sendingEmail}
                style={{ padding: "8px 20px", borderRadius: 4, border: "none", background: sendingEmail ? "#e5e7eb" : "#2563eb", color: sendingEmail ? "#6b7280" : "#fff", cursor: sendingEmail ? "wait" : "pointer", fontSize: 13, fontWeight: 700 }}
              >
                {sendingEmail ? "Enviando..." : "Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toasts={toasts} onClose={closeToast} />

      {/* DOCUMENTO */}
      <main id="print-area" className="cfdi-print doc">
        <h2 className="cfdi-title">
          Detalle de Factura{" "}
          <span className="cfdi-title-accent">
            {detalle.serie}-{detalle.numeroDocumento}
          </span>
        </h2>

        {/* CABECERA */}
        <section className="cfdi-card avoid-break">
          <h3 className="cfdi-section-title">Datos de Cabecera</h3>
          <div className="cfdi-header-grid">
            <DetailItem label="Cliente" value={detalle.claveCliente} />
            <DetailItem label="Nombre" value={detalle.nombreCliente} span={3} />
            <DetailItem
              label="Fecha Factura"
              value={formatDate(detalle.fechaMovimiento)}
            />
            <DetailItem
              label="Método de Pago"
              value={detalle.condicionesPago || "N/A"}
            />
            <DetailItem label="RFC" value={detalle.rfc} />
            <DetailItem
              label="Moneda"
              value={isDollar ? "Dólares (USD)" : "Pesos (MXN)"}
            />
            <DetailItem
              label="Uso CFDI"
              value={`${detalle.usoCfdi} - ${detalle.usoCfdiDescripcion}`}
              span={2}
            />
            {detalle.cotizacion && (
              <div className="cfdi-field span-4 no-print" style={{
                background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6,
                padding: "6px 14px", display: "flex", alignItems: "center", gap: 8,
              }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#92400e" }}>Generada desde cotización:</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#78350f" }}>{detalle.cotizacion}</span>
              </div>
            )}
          </div>
        </section>

        {/* PARTIDAS */}
        <section className="cfdi-card" style={{ marginTop: 20 }}>
          <h3 className="cfdi-section-title">Partidas / Artículos</h3>

          <div className="cfdi-table-wrap">
            <table className="cfdi-table">
              <colgroup>
                <col style={{ width: "14%" }} />
                <col style={{ width: "36%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="th">Clave</th>
                  <th className="th">Descripción</th>
                  <th className="th th-num">Cant.</th>
                  <th className="th">Unidad</th>
                  <th className="th th-num">Precio</th>
                  <th className="th th-num">Importe</th>
                </tr>
              </thead>
              <tbody>
                {detalle.partidas?.map((p, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? "row-alt" : ""}>
                    <td className="td">{p.claveArticulo}</td>
                    <td className="td td-desc">{p.descripcion}</td>
                    <td className="td td-num">
                      {Number(p.cantidad).toLocaleString("es-MX")}
                    </td>
                    <td className="td">
                      {p.unidad}
                      {p.codprodserv && (
                        <span className="codprodserv"> – {p.codprodserv}</span>
                      )}
                    </td>
                    <td className="td td-num">
                      {formatCurrency(p.precio, isDollar)}
                    </td>
                    <td className="td td-num">
                      {formatCurrency(p.importe, isDollar)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* TOTALES */}
          <div className="cfdi-totales avoid-break">
            <div className="cfdi-importe-letra">
              <div className="cfdi-label">Importe con letra</div>
              <div className="cfdi-importe-letra-text">
                {detalle.importeEnLetra}
              </div>
            </div>

            <div className="cfdi-resumen">
              <table className="cfdi-resumen-table">
                <tbody>
                  <tr>
                    <td className="resumen-label">SUBTOTAL</td>
                    <td className="resumen-value">
                      {formatCurrency(subtotal, isDollar)}
                    </td>
                  </tr>
                  <tr>
                    <td className="resumen-label">IVA</td>
                    <td className="resumen-value">
                      {formatCurrency(iva, isDollar)}
                    </td>
                  </tr>
                  <tr>
                    <td className="resumen-label resumen-total">TOTAL</td>
                    <td className="resumen-value resumen-total">
                      {formatCurrency(total, isDollar)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* CFDI */}
        <section className="cfdi-card" style={{ marginTop: 20 }}>
          <h3 className="cfdi-section-title">Datos CFDI</h3>

          <div className="cfdi-grid-2">
            <div>
              <p className="cfdi-mini-label">Folio Fiscal UUID</p>
              <p className="cfdi-mini-value">
                {detalle.folioFiscalUuid || "N/A"}
              </p>

              <p className="cfdi-mini-label">Sello del SAT</p>
              <p className="cfdi-mini-value mono">
                {detalle.selloSat || "N/A"}
              </p>

              <p className="cfdi-mini-label">Sello Digital del CFDI</p>
              <p className="cfdi-mini-value mono">
                {detalle.selloCfd || "N/A"}
              </p>

              <p className="cfdi-mini-label">
                No. Certificado del Emisor (CSD)
              </p>
              <p className="cfdi-mini-value">
                {detalle.noCertificado || "N/A"}
              </p>

              <p className="cfdi-mini-label">
                No. de Serie del Certificado del SAT
              </p>
              <p className="cfdi-mini-value">
                {detalle.noCertificadoSat || "N/A"}
              </p>
            </div>

            <div>
              <p className="cfdi-mini-label">Fecha / Hora Certificación</p>
              <p className="cfdi-mini-value">
                {formatDateTime(detalle.fechaHoraCertificacion)}
              </p>

              <p className="cfdi-mini-label">Cadena Original</p>
              <p className="cfdi-mini-value mono">
                {detalle.cadenaOriginal || "N/A"}
              </p>

              {detalle.qrUrl && (
                <div className="cfdi-qr-block avoid-break">
                  <QRCodeCanvas value={detalle.qrUrl} size={180} />
                  <p className="cfdi-qr-caption">
                    Folio Fiscal (UUID): {detalle.folioFiscalUuid}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        <footer className="cfdi-footer">
          <div>
            Este documento es una representación impresa de un CFDI Versión
            4.0&nbsp;&nbsp; Lugar Expedición:{" "}
            {detalle?.lugarExpedicionCp ?? "N/A"}
          </div>
          <div>
            La reproducción no autorizada de este comprobante constituye un
            delito en los términos de las disposiciones fiscales.
          </div>
        </footer>
      </main>
    </div>
  );
}
